import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  TextField,
  Button,
  Alert,
  Typography,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { adminLogin } from "../../services/auth";
import { useAuth } from "../../Context/AuthContext";
import { getAdminDefaultRoute } from "../../access/getAdminDefaultRoute";
import logo from "../../Assets/logo.png";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("12345678");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      const res = await adminLogin({ username, password });

      const user = res?.data?.data?.user;
      const token = res?.data?.data?.token;

      if (!user || !token) {
        throw new Error("Invalid login response");
      }

      if (user?.type !== "admin") {
        throw new Error("هذا الحساب ليس حساب إدارة");
      }

      login(user, token);
      navigate(getAdminDefaultRoute(user), { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "فشل تسجيل الدخول. الرجاء التحقق من بيانات الدخول";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #2C1810 0%, #1A1410 50%, #4A2C1F 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: "300px",
          height: "300px",
          background: "rgba(212, 175, 55, 0.08)",
          borderRadius: "37% 63% 58% 42% / 52% 56% 44% 48%",
          top: "-50px",
          right: "-50px",
          animation: "blob 7s infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: "250px",
          height: "250px",
          background: "rgba(200, 153, 104, 0.06)",
          borderRadius: "37% 63% 58% 42% / 52% 56% 44% 48%",
          bottom: "-50px",
          left: "-50px",
          animation: "blob 7s infinite 2s",
        },
        "@keyframes blob": {
          "0%, 100%": {
            transform: "translate(0, 0) scale(1)",
          },
          "33%": {
            transform: "translate(30px, -50px) scale(1.1)",
          },
          "66%": {
            transform: "translate(-20px, 50px) scale(0.9)",
          },
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid
          container
          spacing={5}
          alignItems="center"
          justifyContent="center"
          sx={{ direction: "rtl" }}
        >
          {/* Login Card */}
          <Grid item xs={12} sm={10} md={6} lg={4}>
            <Card
              sx={{
                borderRadius: 3,
                padding: 5,
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                animation: "slideUp 0.6s ease-out",
                "@keyframes slideUp": {
                  from: {
                    opacity: 0,
                    transform: "translateY(30px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 4,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: 126, sm: 140 },
                    height: { xs: 126, sm: 140 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(145deg, #F7E7A5 0%, #D4AF37 48%, #B77A38 100%)",
                    borderRadius: "50%",
                    padding: "9px",
                    boxShadow: `
                      0 18px 42px rgba(44, 24, 16, 0.22),
                      0 0 46px rgba(212, 175, 55, 0.34),
                      inset 0 3px 8px rgba(255, 255, 255, 0.36),
                      inset 0 -8px 18px rgba(91, 55, 21, 0.18)
                    `,
                    border: "1px solid rgba(255, 255, 255, 0.48)",
                    animation: "pulse 3s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": {
                        boxShadow: `
                          0 18px 42px rgba(44, 24, 16, 0.22),
                          0 0 46px rgba(212, 175, 55, 0.34),
                          inset 0 3px 8px rgba(255, 255, 255, 0.36),
                          inset 0 -8px 18px rgba(91, 55, 21, 0.18)
                        `,
                      },
                      "50%": {
                        boxShadow: `
                          0 20px 48px rgba(44, 24, 16, 0.24),
                          0 0 62px rgba(212, 175, 55, 0.48),
                          inset 0 3px 8px rgba(255, 255, 255, 0.36),
                          inset 0 -8px 18px rgba(91, 55, 21, 0.18)
                        `,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      overflow: "hidden",
                      backgroundColor: "#3F5044",
                      border: "4px solid rgba(255, 248, 218, 0.88)",
                      boxShadow:
                        "inset 0 0 0 1px rgba(63, 80, 68, 0.22), 0 8px 18px rgba(44, 24, 16, 0.22)",
                    }}
                  >
                    <Box
                      component="img"
                      src={logo}
                      alt="Logo"
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        objectFit: "cover",
                        transform: "scale(1.04)",
                        filter: "drop-shadow(0 3px 5px rgba(0, 0, 0, 0.16))",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Title */}
              <Box sx={{ textAlign: "center", marginBottom: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #2C1810 0%, #8B6F47 100%)",
                    backgroundClip: "text",
                    webkitBackgroundClip: "text",
                    webkitTextFillColor: "transparent",
                    marginBottom: 1,
                  }}
                >
                  لوحة التحكم
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#C89968",
                    fontWeight: 600,
                  }}
                >
                  تسجيل الدخول كمسؤول
                </Typography>
              </Box>

              {/* Form */}
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {/* Username Input */}
                <TextField
                  label="اسم المستخدم"
                  placeholder="أدخل اسم المستخدم"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">👤</InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      background: "#F5E6D3",
                      transition: "all 0.3s ease",
                      "&:hover fieldset": {
                        borderColor: "#D4AF37",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#D4AF37",
                        boxShadow: "0 0 0 4px rgba(212, 175, 55, 0.1)",
                      },
                      "&.Mui-disabled": {
                        background: "#f3f4f6",
                        opacity: 0.6,
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      direction: "ltr",
                      color: "#2C1810",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#8B6F47",
                      "&.Mui-focused": {
                        color: "#D4AF37",
                      },
                    },
                  }}
                />

                {/* Password Input */}
                <TextField
                  label="كلمة المرور"
                  placeholder="أدخل كلمة المرور"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                          edge="end"
                          sx={{ padding: 1 }}
                        >
                          {showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      background: "#F5E6D3",
                      transition: "all 0.3s ease",
                      "&:hover fieldset": {
                        borderColor: "#D4AF37",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#D4AF37",
                        boxShadow: "0 0 0 4px rgba(212, 175, 55, 0.1)",
                      },
                      "&.Mui-disabled": {
                        background: "#f3f4f6",
                        opacity: 0.6,
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      direction: "ltr",
                      color: "#2C1810",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#8B6F47",
                      "&.Mui-focused": {
                        color: "#D4AF37",
                      },
                    },
                  }}
                />

                {/* Error Message */}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: 1.5,
                      animation: "shake 0.4s ease",
                      "@keyframes shake": {
                        "0%, 100%": {
                          transform: "translateX(0)",
                        },
                        "25%": {
                          transform: "translateX(-5px)",
                        },
                        "75%": {
                          transform: "translateX(5px)",
                        },
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                  sx={{
                    background: "linear-gradient(135deg, #D4AF37 0%, #C89968 100%)",
                    padding: "12px",
                    fontSize: 16,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderRadius: 1.5,
                    color: "#2C1810",
                    boxShadow: "0 4px 15px rgba(212, 175, 55, 0.4)",
                    transition: "all 0.3s ease",
                    marginTop: 1,
                    "&:hover:not(:disabled)": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(212, 175, 55, 0.6)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? "جاري التحميل..." : "دخول"}
                </Button>
              </Box>

              {/* Divider */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  margin: "25px 0",
                  color: "#ccc",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    background: "#E8DCC0",
                  }}
                />
                <Typography sx={{ color: "#C89968", fontSize: 13 }}>
                  أو
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    background: "#E8DCC0",
                  }}
                />
              </Box>

              {/* Footer */}
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#8B6F47" }}>
                  العودة إلى{" "}
                  <Box
                    component="a"
                    href="/login"
                    sx={{
                      color: "#D4AF37",
                      textDecoration: "none",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "color 0.3s ease",
                      "&:hover": {
                        color: "#C89968",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    صفحة المستخدم
                  </Box>
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Info Cards (Desktop) */}
          <Grid item xs={0} md={5} sx={{ display: { xs: "none", md: "block" } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                animation: "slideUpDelay 0.8s ease-out",
                "@keyframes slideUpDelay": {
                  from: {
                    opacity: 0,
                    transform: "translateY(30px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              {/* Info Card 1 */}
              <Paper
                sx={{
                  background: "rgba(212, 175, 55, 0.15)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(212, 175, 55, 0.3)",
                  borderRadius: 2,
                  padding: 3,
                  color: "#F5E6D3",
                  boxShadow: "0 8px 32px rgba(212, 175, 55, 0.2)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  ⚙️ إدارة شاملة
                </Typography>
                <Typography sx={{ fontSize: 14, opacity: 0.95, lineHeight: 1.5 }}>
                  تحكم كامل في العمليات والمستخدمين والقوائم
                </Typography>
              </Paper>

              {/* Info Card 2 */}
              <Paper
                sx={{
                  background: "rgba(212, 175, 55, 0.15)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(212, 175, 55, 0.3)",
                  borderRadius: 2,
                  padding: 3,
                  color: "#F5E6D3",
                  boxShadow: "0 8px 32px rgba(212, 175, 55, 0.2)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  📊 تقارير فورية
                </Typography>
                <Typography sx={{ fontSize: 14, opacity: 0.95, lineHeight: 1.5 }}>
                  احصائيات وتقارير مفصلة في الوقت الفعلي
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
